import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Error, WarningTriangle } from '@withjoy/joykit/icons';
import { Box, Flex, useToast } from '@withjoy/joykit';
import { pxToRem } from '@withjoy/joykit/theme';
import { closestCenter } from '@dnd-kit/core';
import SortableList from '@shared/components/SortableList';
import { useTranslation } from '@shared/core';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { CheckIcon } from '@assets/icons';
import { sortByOrderList } from '@shared/utils/array';
import { useUpdateAccommodationPlaceV2Mutation, useUpdateTravelMapComponentMutation } from '../../Accommodations.graphql.generated';
import { useUpdateWebsitePreview } from '@website-editor/hooks/useUpdateWebsitePreview';
import type { AccommodationCard } from '../../Accommodations.types';
import { TravelMapBlockType } from '../../Accommodations.types';
import { useAccommodationsContext } from '../../context/useAccommodationsContext';
import { isCustomPropertyAccommodation } from '../../utils/Accommodations.utils';
import { useCustomComponentStateContext } from '@website-editor/features/custom-page/context/CustomComponentStateContext/CustomComponentState.context';
import { useNavigationPaneContext } from '@website-editor/context/NavigationPaneContext/NavigationPane.context';
import { ACCOMMODATIONS_ACTIONS } from '../../Accommodations.constants';
import { useAdminAccommodationsTelemetry } from '../../Accommodations.telemetry';
import { useSizeObserver } from '@shared/utils/hooks/useSizeObserver';
import { addError } from '@shared/utils/logger';
import { noop } from '@shared/utils/noop';
import { SortableHotelTile } from './SortableHotelTile';
import { HotelTourStep } from '../AccommodationsTour';
import { useAutoPopulatedAccommodations } from '../../hooks/useAutoPopulatedAccommodations';
import { useIsNewlyAddedHotel } from '../../hooks/useIsNewlyAddedHotel';
import { useIsFeatureEnabled } from '@shared/core/featureFlags';
import { useIsVenueMatch } from '../../hooks/useIsVenueMatch';
import { VenueMatchBanner } from '../VenueMatchBanner/VenueMatchBanner';

/** Wraps a hotel tile + optional venue-match banner. */
const HotelTileWithBanner = ({ children, hotel, showBanner }: Readonly<{ children: React.ReactNode; hotel: AccommodationCard; showBanner: boolean }>) => {
  const { toast } = useToast();
  const { tq } = useTranslation('accommodationsAdmin');
  const { isVenueMatch } = useIsVenueMatch(hotel.googlePlaceId);
  const hasRoomBlock = hotel.type === TravelMapBlockType.travelMapAccommodationsRoomblock;
  const [isDismissed, setIsDismissed] = useState(false);
  const telemetry = useAdminAccommodationsTelemetry();
  const { updateWebsitePreview } = useUpdateWebsitePreview();

  const isBannerVisible = showBanner && !isDismissed && isVenueMatch && !hotel.isFavorite && !hotel.isHostStayingHere && !hasRoomBlock;

  const [updateAccommodationPlace, { loading: isUpdating }] = useUpdateAccommodationPlaceV2Mutation({
    refetchQueries: ['AccommodationHotels', 'AccommodationPlacesByCouple'],
    awaitRefetchQueries: true,
    onCompleted: () => {
      updateWebsitePreview();
    },
    onError: error => {
      addError(error, { context: 'HotelTileWithBanner venueMatchBanner tag mutation' });
      toast(tq`Failed to update accommodation. Please try again.`, { icon: <WarningTriangle /> });
    },
  });

  const handleTagAsOurPick = useCallback(() => {
    telemetry.venueMatchBannerTagClicked({ hotelId: hotel.id, tagType: 'ourPick' });
    updateAccommodationPlace({ variables: { payload: { id: hotel.id, isFavorite: true } } });
  }, [hotel.id, updateAccommodationPlace, telemetry]);

  const handleWeAreStayingHere = useCallback(() => {
    telemetry.venueMatchBannerTagClicked({ hotelId: hotel.id, tagType: 'stayingHere' });
    updateAccommodationPlace({ variables: { payload: { id: hotel.id, isHostStayingHere: true } } });
  }, [hotel.id, updateAccommodationPlace, telemetry]);

  const handleDismiss = useCallback(() => {
    telemetry.venueMatchBannerDismissed({ hotelId: hotel.id });
    setIsDismissed(true);
  }, [hotel.id, telemetry]);

  useEffect(() => {
    if (isBannerVisible) {
      telemetry.venueMatchBannerShown({ hotelId: hotel.id });
    }
  }, [isBannerVisible, hotel.id, telemetry]);

  return (
    <Box
      borderRadius={isBannerVisible ? pxToRem(12) : undefined}
      border={isBannerVisible ? '1px solid' : undefined}
      borderColor={isBannerVisible ? 'mono3' : undefined}
    >
      {children}
      {isBannerVisible && (
        <VenueMatchBanner
          onTagAsOurPick={handleTagAsOurPick}
          onWeAreStayingHere={handleWeAreStayingHere}
          onDismiss={handleDismiss}
          isLoading={isUpdating}
        />
      )}
    </Box>
  );
};

type HotelsListProps = Readonly<{
  accommodationHotels: ReadonlyArray<AccommodationCard>;
  blockOrdering?: ReadonlyArray<string>;
  onRemove: (hotel: AccommodationCard) => void;
  /** Pre-existing hotel IDs snapshotted before Suggest CTA auto-population. Hotels NOT in this set are newly added. Null when not in Suggest flow. */
  suggestPreExistingIds: ReadonlySet<string> | null;
  isManageMode: boolean;
  selectedIds: ReadonlySet<string>;
  pendingOrder: ReadonlyArray<AccommodationCard> | null;
  onToggleSelection: (id: string) => void;
  onPendingOrderChange: (items: ReadonlyArray<AccommodationCard>) => void;
}>;

export const HotelsList: React.FC<HotelsListProps> = ({
  accommodationHotels,
  blockOrdering,
  onRemove,
  suggestPreExistingIds,
  isManageMode,
  selectedIds,
  pendingOrder,
  onToggleSelection,
  onPendingOrderChange,
}) => {
  const { updateWebsitePreview } = useUpdateWebsitePreview();
  const { toast } = useToast();
  const [updateTravelMapComponent] = useUpdateTravelMapComponentMutation();
  const { setCustomComponentBlockData, setSelectedHotelPhotos } = useCustomComponentStateContext();
  const { navigateToScreen } = useNavigationPaneContext();
  const telemetry = useAdminAccommodationsTelemetry();
  const { tq } = useTranslation('accommodationsAdmin');

  const { componentData, accommodationsData, isSuperAdmin } = useAccommodationsContext();
  const [venueMatchSuggestionEnabled] = useIsFeatureEnabled('accommodationsVenueMatchSuggestion');
  const { isFirstTimeAutoPopulation } = useAutoPopulatedAccommodations();
  const checkIsNewlyAdded = useIsNewlyAddedHotel(isFirstTimeAutoPopulation);

  const [accommodationsList, setAccommodationsList] = useState<AccommodationCard[]>(Array.from(accommodationHotels));
  const [containerSize, containerRef] = useSizeObserver();
  const containerWidth = containerSize.width || null;

  // Ref mirrors isDragActive state — used in the useEffect guard below to prevent
  // a background Apollo refetch from resyncing items mid-drag, which disrupts
  // @dnd-kit's internal rect tracking and causes `over` to be null on drop.
  const isDragActiveRef = useRef(false);
  const skippedSyncRef = useRef(false);

  useEffect(() => {
    if (!isDragActiveRef.current) {
      setAccommodationsList(Array.from(accommodationHotels));
    } else {
      skippedSyncRef.current = true;
    }
  }, [accommodationHotels]);

  const [isDragActive, setIsDragActive] = useState(false);

  // Catch-up: when drag ends, apply any prop update that was skipped mid-drag.
  // Runs as a separate effect (not in handlers) to avoid interfering with @dnd-kit cleanup.
  useEffect(() => {
    if (!isDragActive && skippedSyncRef.current) {
      skippedSyncRef.current = false;
      setAccommodationsList(Array.from(accommodationHotels));
    }
  }, [isDragActive, accommodationHotels]);

  const handleDragStart = useEventCallback(() => {
    isDragActiveRef.current = true;
    setIsDragActive(true);
  });

  const handleDragCancel = useEventCallback(() => {
    isDragActiveRef.current = false;
    setIsDragActive(false);
  });

  // SortableList expects a mutable array; copy pendingOrder when active so the prop satisfies T[].
  const displayedList = useMemo<AccommodationCard[]>(
    () => (isSuperAdmin && isManageMode && pendingOrder ? Array.from(pendingOrder) : accommodationsList),
    [isSuperAdmin, isManageMode, pendingOrder, accommodationsList],
  );

  // Drag is always enabled. Manage mode (super admin + isManageMode) batches the reorder; otherwise it auto-saves on drop.
  const isBatchedReorder = !!isSuperAdmin && isManageMode;

  const handleDragEnd = useEventCallback(async (dragData: { fromIndex: number; toIndex: number; items: AccommodationCard[]; reorderedItem: AccommodationCard | undefined }) => {
    isDragActiveRef.current = false;
    setIsDragActive(false);

    // SortableList passes empty items when dnd-kit can't detect a drop target
    if (!dragData.items.length) return;

    const hasSortOrderChanged = !dragData.items.every((item, index) => item.id === displayedList[index].id);
    if (!hasSortOrderChanged) return;

    // Favorite-first invariant: snap back and toast if the drag displaces the favorite.
    const favoriteItem = displayedList.find(item => item.isFavorite);
    if (favoriteItem && !dragData.items[0].isFavorite) {
      toast(tq`Favorite hotel must remain first in the list`, { icon: <Error size={24} /> });
      if (!isBatchedReorder) {
        const accOrder = sortByOrderList(accommodationsList, blockOrdering ? Array.from(blockOrdering) : undefined);
        setAccommodationsList(accOrder);
      }
      return;
    }

    // List shape changed server-side during drag — discard the reorder and fall back to fresh data.
    if (skippedSyncRef.current) {
      const latestHotelIds = new Set(accommodationHotels.map(hotel => hotel.id));
      const reorderedIds = dragData.items.map(item => item.id);
      const listShapeChanged = reorderedIds.length !== accommodationHotels.length || reorderedIds.some(id => !latestHotelIds.has(id));
      if (listShapeChanged) {
        skippedSyncRef.current = false;
        setAccommodationsList(Array.from(accommodationHotels));
        return;
      }
    }

    skippedSyncRef.current = false;

    if (isBatchedReorder) {
      // Treatment + Manage mode: stage reorder; persist on Save.
      onPendingOrderChange(dragData.items);
      return;
    }

    // Control arm: legacy auto-save on drop.
    setAccommodationsList(dragData.items);
    try {
      const response = await updateTravelMapComponent({
        variables: {
          payload: {
            id: componentData?.id,
            sortOrder: componentData?.sortOrder ?? 1,
            blockOrdering: dragData.items.map(item => item.id),
          },
        },
        refetchQueries: ['PageContainerBySlug'],
      });
      if (response.errors) {
        addError(response.errors[0], { context: 'Accommodations failed to update sort order' });
        toast(tq`Failed to update order`, { icon: <WarningTriangle /> });
      }
      if (response.data) {
        updateWebsitePreview();
        toast(tq`Order updated`, { icon: <CheckIcon /> });
      }
    } catch (error) {
      addError(error, { context: 'Accommodations failed to update sort order' });
      toast(tq`Failed to update order`, { icon: <WarningTriangle /> });
    }
  });

  const handleHotelEdit = useEventCallback((hotel: AccommodationCard) => {
    // Clear stale catalog photos from a previous edit; the edit form fetches this hotel's photos on open.
    switch (hotel.type) {
      case TravelMapBlockType.travelMapAccommodationsRoomblock: {
        const roomBlockToEdit = accommodationsData?.accommodationPlacesV2.find(i => i.id === hotel.id);
        if (!roomBlockToEdit) {
          addError('Unable to find room block error');
          return;
        }
        telemetry.editItemClicked('accommodations.RoomBlock', { itemType: hotel.type, isSuperAdmin: !!isSuperAdmin });
        setSelectedHotelPhotos([]);
        setCustomComponentBlockData(roomBlockToEdit);
        if (isCustomPropertyAccommodation(roomBlockToEdit)) {
          navigateToScreen(ACCOMMODATIONS_ACTIONS.ACCOMMODATIONS_CUSTOM_PROPERTY_EDIT);
        } else {
          navigateToScreen(ACCOMMODATIONS_ACTIONS.ACCOMMODATIONS_HOTEL_EDIT);
        }
        break;
      }
      case TravelMapBlockType.travelMapAccommodationsHotel: {
        const hotelToEdit = accommodationsData?.accommodationPlacesV2.find(i => i.id === hotel.id);
        if (!hotelToEdit) {
          addError('Unable to find hotel error');
          return;
        }
        setSelectedHotelPhotos([]);
        setCustomComponentBlockData(hotelToEdit);
        navigateToScreen(ACCOMMODATIONS_ACTIONS.ACCOMMODATIONS_HOTEL_EDIT);
        break;
      }
      case TravelMapBlockType.travelMapAccommodationsCustom: {
        const customPropToEdit = accommodationsData?.accommodationPlacesV2.find(i => i.id === hotel.id);
        if (!customPropToEdit) {
          addError('Unable to find custom property error');
          return;
        }
        setCustomComponentBlockData(customPropToEdit);
        navigateToScreen(ACCOMMODATIONS_ACTIONS.ACCOMMODATIONS_CUSTOM_PROPERTY_EDIT);
        break;
      }
      default: {
        addError(`Unhandled accommodation type: ${hotel.type}`);
        break;
      }
    }
  });

  const handleHotelRemove = useEventCallback((hotel: AccommodationCard) => {
    onRemove(hotel);
  });

  return (
    <Flex
      ref={containerRef}
      flexDirection="column"
      gap={isManageMode ? 4 : 7}
    >
      <SortableList
        items={displayedList}
        renderItem={(hotel, index) => (
          <HotelTourStep
            key={hotel.id}
            hotel={hotel}
            hotelIndex={index}
          >
            <HotelTileWithBanner
              hotel={hotel}
              showBanner={venueMatchSuggestionEnabled && !isManageMode}
            >
              <SortableHotelTile
                hotel={hotel}
                isDragActive={isDragActive}
                containerWidth={containerWidth}
                isNewlyAdded={suggestPreExistingIds ? !suggestPreExistingIds.has(hotel.id) : checkIsNewlyAdded(hotel.autoSuggestionRecommendation?.venueGooglePlaceId)}
                onEdit={handleHotelEdit}
                onRemove={handleHotelRemove}
                isManageMode={isManageMode}
                isSelected={selectedIds.has(hotel.id)}
                onToggleSelection={onToggleSelection}
              />
            </HotelTileWithBanner>
          </HotelTourStep>
        )}
        onChange={noop}
        hasDragHandle={true}
        handleDragStart={handleDragStart}
        handleDragEnd={handleDragEnd}
        handleDragCancel={handleDragCancel}
        adjustOverlayScale={false}
        dragPlaceholderOpacity={0}
        collisionDetection={closestCenter}
      />
    </Flex>
  );
};
